import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ConfirmModalProps {
  isOpen: boolean;
  isPrompt?: boolean;
  title: string;
  message: string;
  placeholder?: string;
  required?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'neutral';
  onConfirm: (value?: string) => void;
  onCancel: () => void;
}

const variantStyles = {
  danger: {
    icon: 'bg-red-400/10 text-red-500',
    button: 'bg-red-500 hover:bg-red-600 text-white',
  },
  warning: {
    icon: 'bg-amber-400/10 text-amber-500',
    button: 'bg-amber-500 hover:bg-amber-600 text-white',
  },
  neutral: {
    icon: 'bg-slate-400/10 text-slate-500',
    button: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
};

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  isPrompt = false,
  title,
  message,
  placeholder,
  required = false,
  confirmLabel,
  cancelLabel,
  variant = 'danger',
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = React.useState('');
  
  React.useEffect(() => {
    if (isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const styles = variantStyles[variant];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div
        className="bg-white dark:bg-dark-card rounded-2xl w-full max-w-sm p-6 border border-slate-200 dark:border-dark-border shadow-2xl transition-colors"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-message"
      >
        <div className={`flex items-center gap-3 mb-4 ${styles.icon}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center`}>
            <AlertTriangle className="w-6 h-6" />
          </div>
          <h3 id="confirm-title" className="text-lg font-bold text-slate-900 dark:text-white">
            {title}
          </h3>
        </div>
        <p id="confirm-message" className="text-slate-600 dark:text-slate-400 text-sm mb-6">
          {message}
        </p>
        {isPrompt && (
          <div className="mb-6">
            <input
              type="text"
              autoFocus
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={placeholder || ''}
              className="w-full p-3 rounded-xl border border-slate-200 dark:border-dark-border bg-slate-50 dark:bg-dark-input text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
        )}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl border border-slate-300 dark:border-dark-border text-slate-700 dark:text-slate-300 font-bold hover:bg-slate-100 dark:hover:bg-dark-border/50 transition-colors"
          >
            {cancelLabel || t('modal.cancel')}
          </button>
          <button
            onClick={() => onConfirm(isPrompt ? inputValue : undefined)}
            disabled={isPrompt && required && !inputValue.trim()}
            className={`flex-1 py-2.5 rounded-xl font-bold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${styles.button}`}
          >
            {confirmLabel || t('modal.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};
